(() => {
  const className = 'campus-purchase-options-visible';
  let observer;

  function watchPostscriptTeaser() {
    const iframe = document.getElementById('ps__widget');
    if (!iframe) {
      const discovery = new MutationObserver(() => {
        if (document.getElementById('ps__widget')) {
          discovery.disconnect();
          watchPostscriptTeaser();
        }
      });
      discovery.observe(document.body, { childList: true, subtree: true });
      return;
    }

    const markTeaser = () => {
      const rect = iframe.getBoundingClientRect();
      const isTeaser = rect.width > 0 && rect.height > 0 &&
        Math.max(rect.width, rect.height) <= 300 &&
        Math.min(rect.width, rect.height) <= 150;
      iframe.classList.toggle('campus-postscript-teaser', isTeaser);
    };
    markTeaser();
    new MutationObserver(markTeaser).observe(iframe, {
      attributes: true,
      attributeFilter: ['style'],
    });
  }

  function observePurchaseOptions() {
    observer?.disconnect();
    document.body.classList.remove(className);

    const purchaseOptions = document.querySelector(
      '.product-single__form .loop-subscription-container'
    );
    if (!purchaseOptions) return;

    observer = new IntersectionObserver((entries) => {
      document.body.classList.toggle(className, entries[0].isIntersecting);
    });
    observer.observe(purchaseOptions);
  }

  const packageUnits = {
    pack: 'packs', packs: 'packs', bar: 'bars', bars: 'bars',
    can: 'cans', cans: 'cans', bottle: 'bottles', bottles: 'bottles',
    bag: 'bags', bags: 'bags', carton: 'cartons', cartons: 'cartons',
    stick: 'sticks', sticks: 'sticks', packet: 'packets', packets: 'packets',
    box: 'boxes', boxes: 'boxes',
  };

  function packageSize(value) {
    const match = String(value || '').trim().match(/^(\d+)\s*-?\s*(pack|packs|bar|bars|can|cans|bottle|bottles|bag|bags|carton|cartons|stick|sticks|packet|packets|box|boxes)$/i);
    return match ? { count: Number(match[1]), unit: packageUnits[match[2].toLowerCase()] } : null;
  }

  function individualEquivalent(product, variant) {
    if (/\b(mixed|variety|assorted|assortment|sampler|bundle|kit)\b/i.test(`${product.title} ${variant.title}`)) {
      return null;
    }
    const options = variant.options || [];
    const packageOptions = options.map((value, index) => ({ ...packageSize(value), index }))
      .filter((item) => item.count > 1);
    if (packageOptions.length !== 1) return null;

    const pack = packageOptions[0];
    const matches = product.variants.filter((candidate) => {
      const unit = packageSize(candidate.options?.[pack.index]);
      return candidate.available && Number.isInteger(Number(candidate.price)) && Number(candidate.price) > 0 &&
        unit?.count === 1 && unit.unit === pack.unit &&
        candidate.options.length === options.length &&
        candidate.options.every((option, index) =>
          index === pack.index || option === options[index]);
    });
    return matches.length === 1
      ? { variant: matches[0], count: pack.count, unit: pack.unit }
      : null;
  }

  function activeUsd() {
    return window.Shopify?.currency?.active === 'USD' &&
      Number(window.Shopify.currency.rate) === 1;
  }

  function formatUsd(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency', currency: 'USD',
    }).format(cents / 100);
  }

  function renderEffectiveHeader(form, fieldset, product) {
    if (!activeUsd()) return;
    const variantId = Number(form.querySelector('[name="id"]')?.value);
    const variant = product.variants.find((item) => Number(item.id) === variantId);
    if (!variant) return;
    const planId = Number(fieldset.dataset.sellingPlanId);
    const allocation = variant.selling_plan_allocations?.find((item) =>
      Number(item.selling_plan_id) === planId);
    if (planId && !allocation) return;
    const cents = Number(allocation?.per_delivery_price ?? variant.price);
    if (!Number.isInteger(cents)) return;

    const header = form.closest('.product-section')?.querySelector('[data-product-price]');
    if (!header) return;
    const visible = header.querySelector('[aria-hidden="true"]');
    const accessible = header.querySelector('.visually-hidden');
    if (visible && accessible) {
      visible.innerHTML = window.theme?.Currency?.formatMoney(cents, window.theme?.settings?.moneyFormat) || formatUsd(cents);
      accessible.textContent = formatUsd(cents);
    } else {
      header.textContent = formatUsd(cents);
    }
  }

  function renderSavings(form, fieldset, product) {
    const widget = fieldset.closest('.loop-subscription-container');
    let summary = widget.querySelector('.campus-savings-summary');
    if (!summary) {
      summary = document.createElement('p');
      summary.className = 'campus-savings-summary';
      summary.setAttribute('aria-live', 'polite');
      fieldset.insertAdjacentElement('afterend', summary);
    }
    widget.querySelectorAll('.campus-primary-total-offer').forEach((group) => {
      group.classList.remove('campus-primary-total-offer');
    });

    const selectedId = Number(form.querySelector('[name="id"]')?.value);
    const variant = product.variants.find((item) => Number(item.id) === selectedId);
    const oneTimePrice = Number(variant?.price);
    const planId = Number(fieldset.dataset.sellingPlanId);
    const allocation = variant?.selling_plan_allocations?.find((item) =>
      Number(item.selling_plan_id) === planId);
    const subscribing = Boolean(planId && allocation);
    const subscriptionPrice = Number(allocation?.per_delivery_price);
    const quantity = Number(form.elements.namedItem('quantity')?.value || 1);

    if (!variant?.available || !Number.isInteger(oneTimePrice) ||
        (planId && !allocation) ||
        (subscribing && !Number.isInteger(subscriptionPrice)) ||
        !Number.isSafeInteger(quantity) || quantity < 1) {
      summary.hidden = true;
      return;
    }

    const equivalent = individualEquivalent(product, variant);
    const baseline = equivalent ? Number(equivalent.variant.price) * equivalent.count : oneTimePrice;
    const packSaving = equivalent ? baseline - oneTimePrice : 0;
    const subscriptionSaving = subscribing ? oneTimePrice - subscriptionPrice : 0;
    const effectivePrice = subscribing ? subscriptionPrice : oneTimePrice;
    const hasPackSaving = packSaving > 0;
    const comparison = hasPackSaving ? baseline : oneTimePrice;
    const totalSaving = Math.max(0, (comparison - effectivePrice) * quantity);
    if (!Number.isInteger(baseline) || baseline <= 0 || totalSaving <= 0 ||
        (subscribing && subscriptionSaving <= 0)) {
      summary.hidden = true;
      return;
    }

    // Product JSON prices are in the shop's USD currency. Avoid a mixed-currency claim.
    if (!activeUsd()) {
      summary.hidden = true;
      return;
    }
    const selectedGroup = subscribing
      ? fieldset.querySelector('.loop-subscription-group-radio:checked')?.closest('.loop-subscription-group')
      : null;
    if (selectedGroup) {
      const price = selectedGroup.querySelector('.loop-subscription-group-price-container');
      if (price) {
        price.insertAdjacentElement('afterend', summary);
        selectedGroup.classList.add('campus-primary-total-offer');
      }
    } else {
      fieldset.insertAdjacentElement('afterend', summary);
    }
    const money = formatUsd;
    const percent = Math.floor((totalSaving * 100) / (comparison * quantity));
    let headline;
    let detail = '';
    if (hasPackSaving) {
      headline = `Save ${money(totalSaving)} total (${percent}%) vs ${equivalent.count * quantity} individual ${equivalent.unit}`;
      detail = subscribing
        ? `${money(packSaving * quantity)} pack + ${money(subscriptionSaving * quantity)} subscription savings on your first order.`
        : `At the selected one-time quantity.`;
    } else {
      headline = `Save ${money(totalSaving)} with subscription (${percent}%)`;
      detail = `vs this pack one-time, on your first order${quantity > 1 ? ` for ${quantity} packs` : ''}.`;
    }

    summary.replaceChildren();
    const strong = document.createElement('strong');
    strong.textContent = headline;
    const small = document.createElement('small');
    small.textContent = detail;
    summary.append(strong, small);
    summary.hidden = false;
  }

  function setupSavings() {
    const form = document.querySelector('.product-single__form');
    const fieldset = form?.querySelector('#loop-selling-plan-fieldset');
    if (!fieldset) return;

    let productSource;
    let product;
    let queued = false;
    const refresh = () => {
      queued = false;
      if (!fieldset.dataset.product) return;
      if (fieldset.dataset.product !== productSource) {
        productSource = fieldset.dataset.product;
        try {
          product = JSON.parse(productSource);
        } catch {
          product = null;
        }
      }
      if (!product?.variants) return;

      const selectedId = form.querySelector('[name="id"]')?.value;
      if (selectedId && selectedId !== fieldset.dataset.selectedVariantId &&
          typeof window.loopInit === 'function' && window.loopPropsUI) {
        window.loopInit({ productId: product.id, product, variantId: selectedId });
        return;
      }

      renderEffectiveHeader(form, fieldset, product);
      renderSavings(form, fieldset, product);
    };
    const queueRefresh = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(refresh);
    };

    new MutationObserver(queueRefresh).observe(fieldset, {
      attributes: true,
      attributeFilter: ['data-product', 'data-selected-variant-id', 'data-selling-plan-id', 'data-selling-plan-group-id'],
    });
    document.addEventListener('variant:change', queueRefresh);
    document.addEventListener('shopify:section:load', queueRefresh);
    const quantityInput = form.elements.namedItem('quantity');
    quantityInput?.addEventListener('input', queueRefresh);
    quantityInput?.addEventListener('change', queueRefresh);
    queueRefresh();
  }

  watchPostscriptTeaser();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observePurchaseOptions, { once: true });
    document.addEventListener('DOMContentLoaded', setupSavings, { once: true });
  } else {
    observePurchaseOptions();
    setupSavings();
  }

  document.addEventListener('shopify:section:load', observePurchaseOptions);
})();
